import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode
} from 'react'

import { useAuth0 } from '@auth0/auth0-react'

import { AppLoadingScreen } from '@/components/primitives/principal-loading'

import {
  fetchUserByEmail,
  resolveTractianCompanyId,
  resolveTractianUserId
} from '@/lib/authentication/fetch-user-by-email'
import { setTractianUserId as setTractianUserIdRef } from '@/lib/authentication/tractian-user-id-ref'
import { postHogService } from '@/lib/posthog'

type TractianUserContextValue = {
  tractianUserId: string
  companyId: string
  isReady: boolean
}

export { getTractianUserId } from '@/lib/authentication/tractian-user-id-ref'

const TractianUserContext = createContext<TractianUserContextValue | null>(null)

type TractianUserProviderProps = {
  children: ReactNode
}

export function TractianUserProvider({ children }: TractianUserProviderProps) {
  const { user } = useAuth0()
  const userRef = useRef(user)
  const [tractianUserId, setTractianUserId] = useState('')
  const [companyId, setCompanyId] = useState('')
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    userRef.current = user
  }, [user])

  useEffect(() => {
    if (!user?.email) {
      setTractianUserIdRef(undefined)
      setTractianUserId('')
      setCompanyId('')
      setIsReady(true)
      return
    }

    let cancelled = false

    fetchUserByEmail(user.email)
      .then(tractianUser => {
        if (cancelled) return
        const resolvedId = resolveTractianUserId(tractianUser) ?? ''
        setTractianUserIdRef(resolvedId || undefined)
        setTractianUserId(resolvedId)
        setCompanyId(resolveTractianCompanyId(tractianUser) ?? '')
        setIsReady(true)
        postHogService.identify({
          id: resolvedId || undefined,
          name: userRef.current?.name,
          email: userRef.current?.email
        })
      })
      .catch(() => {
        if (!cancelled) {
          setTractianUserIdRef(undefined)
          setTractianUserId('')
          setCompanyId('')
          setIsReady(true)
        }
      })

    return () => {
      cancelled = true
    }
  }, [user?.email])

  if (!isReady) return <AppLoadingScreen />

  return (
    <TractianUserContext.Provider value={{ tractianUserId, companyId, isReady }}>
      {children}
    </TractianUserContext.Provider>
  )
}

export function useTractianUser() {
  const ctx = useContext(TractianUserContext)
  if (!ctx) {
    throw new Error('useTractianUser must be used within a TractianUserProvider')
  }
  return ctx
}
